import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Button, Paper, TextField, Typography } from '@material-ui/core';
import { formatMoney } from '../../helpers/invoiceHelpers';
import RefundLinesTable from './RefundLinesTable';
import RefundSummary from './RefundSummary';
import BudgetMeter from './BudgetMeter';
import ConfirmRefundDialog from './ConfirmRefundDialog';
import { useRefundSelection } from './useRefundSelection';
import { keys, segmentLabel, MAX_NOTE_LENGTH } from './constants';

const styles = ({ spacing: { unit }, palette }) => ({
  warning: {
    padding: unit * 2,
    marginBottom: unit * 3,
    backgroundColor: '#fff4d6',
    color: '#8a6d00',
  },
  warningTitle: {
    fontWeight: 700,
    color: 'inherit',
  },
  columns: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${unit * 3}px`,
    alignItems: 'flex-start',
  },
  leftCol: {
    flexGrow: 1,
    flexBasis: 480,
    minWidth: 0,
  },
  rightCol: {
    width: 360,
    flexGrow: 0,
    flexShrink: 0,
    position: 'sticky',
    top: unit * 2,
  },
  card: {
    padding: unit * 2,
    marginBottom: unit * 3,
    boxSizing: 'border-box',
  },
  lineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: unit,
  },
  counter: {
    textAlign: 'right',
    display: 'block',
    marginTop: unit / 2,
  },
  actionBar: {
    position: 'sticky',
    bottom: 0,
    marginTop: unit * 3,
    padding: `${unit * 2}px 0`,
    backgroundColor: palette.grey[50],
    borderTop: `1px solid ${palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: `${unit * 2}px`,
  },
  actionSpacer: {
    marginLeft: 'auto',
  },
});

// Editable refund form: holds the per-line allocation + note locally (keyed per-open by the
// parent so state resets). Confirm is gated by the total budget + per-rate caps (isValid).
const RefundForm = ({
  classes,
  translate,
  items,
  locale,
  currency,
  currencySymbol,
  decimals,
  refundableAmount,
  refundableByRate,
  invoiceLabel,
  isSubmitting,
  onSubmit,
  onRequestClose,
  onDirtyChange,
}) => {
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const {
    lines,
    selectedCount,
    isValid,
    isOverBudget,
    overRates,
    overRefs,
    selectedTotal,
    remaining,
    refundableAmount: cap,
    summary,
    payloadItems,
    setAmount,
    toggleLine,
    clearAll,
  } = useRefundSelection(items, {
    decimals,
    refundableAmount,
    refundableByRate,
    segmentLabel: name => segmentLabel(translate, name),
  });

  useEffect(() => onDirtyChange(selectedCount > 0), [selectedCount, onDirtyChange]);

  return (
    <>
      <Paper className={classes.warning} elevation={0}>
        <Typography variant="subheading" className={classes.warningTitle}>
          {translate(keys.warningTitle)}
        </Typography>
        <Typography variant="body2" color="inherit">
          {translate(keys.warningBody)}
        </Typography>
      </Paper>

      <div className={classes.columns}>
        <div className={classes.leftCol}>
          <Paper className={classes.card}>
            <div className={classes.lineHeader}>
              <Typography variant="subheading">{translate(keys.lineItemsTitle)}</Typography>
              <Typography variant="body2" color="textSecondary">
                {translate(keys.selected, { count: selectedCount })}
              </Typography>
            </div>
            <RefundLinesTable
              translate={translate}
              lines={lines}
              locale={locale}
              currency={currency}
              currencySymbol={currencySymbol}
              decimals={decimals}
              disabled={isSubmitting}
              isOverBudget={isOverBudget}
              overRefs={overRefs}
              selectedCount={selectedCount}
              onToggleLine={toggleLine}
              onChangeAmount={setAmount}
              onClearAll={clearAll}
            />
          </Paper>
          <Paper className={classes.card}>
            <TextField
              id="refundByItem-note-field"
              label={translate(keys.noteLabel)}
              value={note}
              onChange={e => setNote(e.target.value)}
              multiline
              rows={4}
              fullWidth
              disabled={isSubmitting}
              helperText={translate(keys.noteCounter, { count: note.length, max: MAX_NOTE_LENGTH })}
              inputProps={{ maxLength: MAX_NOTE_LENGTH }}
              FormHelperTextProps={{ className: classes.counter }}
            />
          </Paper>
        </div>

        <div className={classes.rightCol}>
          <Paper className={classes.card}>
            <BudgetMeter
              translate={translate}
              refundableAmount={cap}
              selectedTotal={selectedTotal}
              remaining={remaining}
              isOverBudget={isOverBudget}
              overRates={overRates}
              locale={locale}
              currency={currency}
            />
          </Paper>
          <Paper className={classes.card}>
            <RefundSummary translate={translate} summary={summary} locale={locale} currency={currency} />
          </Paper>
        </div>
      </div>

      <div className={classes.actionBar}>
        <Button
          id="refundByItem-cancel-button"
          className={classes.actionSpacer}
          variant="outlined"
          onClick={onRequestClose}
          disabled={isSubmitting}
        >
          {translate(keys.cancel)}
        </Button>
        <Button
          id="refundByItem-confirm-button"
          variant="contained"
          color="primary"
          onClick={() => setConfirmOpen(true)}
          disabled={isSubmitting || !isValid}
        >
          {summary.totalTTC > 0
            ? translate(keys.confirmWithTotal, { total: formatMoney(summary.totalTTC, locale, currency) })
            : translate(keys.confirm)}
        </Button>
      </div>

      <ConfirmRefundDialog
        open={confirmOpen}
        translate={translate}
        totalTTC={summary.totalTTC}
        locale={locale}
        currency={currency}
        invoiceLabel={invoiceLabel}
        isSubmitting={isSubmitting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => onSubmit({ note, refundItems: payloadItems, receipt: { summary, note } })}
      />
    </>
  );
};

RefundForm.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locale: PropTypes.string,
  currency: PropTypes.string,
  currencySymbol: PropTypes.string,
  decimals: PropTypes.number,
  refundableAmount: PropTypes.number,
  refundableByRate: PropTypes.arrayOf(PropTypes.shape({})),
  invoiceLabel: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func.isRequired,
};

export const RefundFormTest = RefundForm;

export default withStyles(styles)(RefundForm);
